import type { FileUIPart } from 'ai'
import { nanoid } from 'nanoid'
import type { Dispatch, RefObject, SetStateAction } from 'react'

import {
  detectEmailSourceFile,
  stageEmailSourceAttachment,
} from '@/lib/email-conversion/source-formats/read-email-source-file'
import type { ChatFileScope } from '@/lib/shared/blob/chat-file-paths'
import { getErrorMessage } from '@/lib/shared/errors/error-message'
import { uploadUserFile } from '@/lib/uploads/upload-user-file'

type StagedAttachment = {
  url: string
  mediaType: string
  filename: string
  previewText?: string
}

/**
 * Email source files (.html/.mjml/.eml/React Email .jsx/.tsx) never upload:
 * the browser reads them and the part carries a `data:` URL, so the source
 * never lands on the CDN. Everything else takes the upload pipeline; a
 * document goes to the private store under `chatFileScope`.
 */
function stageAttachmentFile(
  file: File,
  chatFileScope: ChatFileScope | undefined
): Promise<StagedAttachment> {
  return detectEmailSourceFile(file)
    ? stageEmailSourceAttachment(file)
    : uploadUserFile(file, undefined, chatFileScope ? { chatFileScope } : {})
}

export type PromptInputFilePart = FileUIPart & {
  previewText?: string
  isHtmlAttachment?: boolean
  uploadStatus?: 'uploading' | 'uploaded' | 'error'
  /**
   * Why `uploadStatus` is `'error'`, in the pipeline's own words (the server
   * verdict — "took too long to convert", "isn't a supported image"). Shown
   * on the chip so the customer knows whether to re-save the file or retry.
   */
  uploadError?: string
  file?: File
}

export type PromptInputAttachment = PromptInputFilePart & { id: string }

export type PromptInputAttachmentInput =
  | File
  | {
      file: File
      previewText?: string
      isHtmlAttachment?: boolean
    }
  | {
      url: string
      mediaType: string
      filename?: string
    }

export type PromptInputAttachmentUploadBehavior = 'upload' | 'local'

/** A functional update of a composer's attachment list. */
export type PromptInputAttachmentsUpdater = (
  prev: Array<PromptInputAttachment>
) => Array<PromptInputAttachment>

/**
 * Attachment state owned OUTSIDE the `PromptInput` — for a composer whose
 * staged files must outlive its own mount (the global chat keeps them per
 * chat, see `components/canvas/chat-composer-uploads.ts`).
 */
export type PromptInputAttachmentsState = {
  files: Array<PromptInputAttachment>
  setFiles: Dispatch<SetStateAction<Array<PromptInputAttachment>>>
  /**
   * Lands a finished (or failed) upload. The owner applies it wherever the
   * attachment lives when the upload settles, which may no longer be this
   * composer.
   */
  settleUpload: (updater: PromptInputAttachmentsUpdater) => void
}

export type AttachmentsContext = {
  files: Array<PromptInputAttachment>
  add: (files: Array<PromptInputAttachmentInput> | FileList) => void
  remove: (id: string) => void
  clear: () => void
  isUploading: boolean
  hasUploadError: boolean
  openFileDialog: () => void
  fileInputRef: RefObject<HTMLInputElement | null>
}

export function isAttachmentDescriptor(
  value: PromptInputAttachmentInput
): value is Extract<PromptInputAttachmentInput, { file: File }> {
  return 'file' in value
}

export function isUploadedAttachment(
  value: PromptInputAttachmentInput
): value is Extract<PromptInputAttachmentInput, { url: string }> {
  return typeof value === 'object' && value !== null && 'url' in value
}

export function normalizeAttachmentInput(
  input: PromptInputAttachmentInput,
  uploadBehavior: PromptInputAttachmentUploadBehavior
): PromptInputAttachment {
  if (isUploadedAttachment(input)) {
    return {
      id: nanoid(),
      type: 'file',
      url: input.url,
      mediaType: input.mediaType,
      ...(input.filename ? { filename: input.filename } : {}),
      uploadStatus: 'uploaded',
    }
  }
  const file = isAttachmentDescriptor(input) ? input.file : input
  const objectUrl = URL.createObjectURL(file)
  return {
    id: nanoid(),
    type: 'file',
    url: objectUrl,
    mediaType: file.type,
    filename: file.name,
    uploadStatus: uploadBehavior === 'upload' ? 'uploading' : 'uploaded',
    file,
    ...(isAttachmentDescriptor(input) && input.previewText
      ? { previewText: input.previewText }
      : {}),
    ...(isAttachmentDescriptor(input) && input.isHtmlAttachment
      ? { isHtmlAttachment: true }
      : {}),
  }
}

export function scheduleAttachmentUploads(args: {
  items: Array<PromptInputAttachment>
  inputs: Array<PromptInputAttachmentInput>
  uploadBehavior: PromptInputAttachmentUploadBehavior
  update: (updater: PromptInputAttachmentsUpdater) => void
  /** The org and brand a document attachment uploads under (private store). */
  chatFileScope: ChatFileScope | undefined
}) {
  if (args.uploadBehavior !== 'upload') {
    return
  }

  args.items.forEach((item, index) => {
    const input = args.inputs[index]
    if (isUploadedAttachment(input)) {
      return
    }
    const file = isAttachmentDescriptor(input) ? input.file : input

    void stageAttachmentFile(file, args.chatFileScope)
      .then((uploaded) => {
        args.update((prev) =>
          prev.map((attachment) => {
            if (attachment.id !== item.id) {
              return attachment
            }
            if (attachment.url.startsWith('blob:')) {
              URL.revokeObjectURL(attachment.url)
            }
            // Images are converted post-upload (svg → png, animated webp
            // → gif), so the part's mediaType/filename must follow the
            // converted blob, not the original File.
            return {
              ...attachment,
              url: uploaded.url,
              mediaType: uploaded.mediaType,
              filename: uploaded.filename,
              ...(uploaded.previewText
                ? { previewText: uploaded.previewText }
                : {}),
              uploadStatus: 'uploaded',
            }
          })
        )
      })
      .catch((error: unknown) => {
        // The upload's own reason when it gave one; without one the chip
        // says it generically, in the reader's language (`ComposerAttachments`).
        const reason = getErrorMessage(error, '')
        args.update((prev) =>
          prev.map((attachment) =>
            attachment.id === item.id
              ? {
                  ...attachment,
                  uploadStatus: 'error',
                  ...(reason ? { uploadError: reason } : {}),
                }
              : attachment
          )
        )
      })
  })
}
